import { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Banner fixo no topo indicando ambiente não-produção.
 *
 * Detecção: VITE_ENVIRONMENT (passada no build) com fallback no hostname
 * (subdomínio ≠ apex). Garante que mesmo sem env var configurada, ambientes
 * não-produção sinalizem para o usuário.
 *
 * Sem botão de fechar — a sinalização precisa ser persistente para evitar
 * confusão de "achei que era prod".
 *
 * Layout: o banner é `position: fixed top-0` e expõe a altura via CSS variable
 * `--env-banner-h` no root. Outros elementos `fixed top-0` (LandingPage header,
 * OfflineBanner, StaticPage header) usam `top-[var(--env-banner-h,0px)]` para
 * ficarem empurrados abaixo. O conteúdo no fluxo normal é empurrado via
 * `padding-top` no body.
 */

type EnvKey = 'staging' | 'qa' | 'demo' | 'development';

interface EnvConfig {
  label: string;
  bg: string;
  text: string;
}

const ENV_CONFIG: Record<EnvKey, EnvConfig> = {
  staging: {
    label: 'Ambiente de homologação — dados não são de produção',
    bg: 'bg-amber-400',
    text: 'text-amber-950',
  },
  qa: {
    label: 'Ambiente de QA — dados não são de produção',
    bg: 'bg-amber-400',
    text: 'text-amber-950',
  },
  demo: {
    label: 'Ambiente de demonstração — dados fictícios',
    bg: 'bg-sky-400',
    text: 'text-sky-950',
  },
  development: {
    label: 'Ambiente de desenvolvimento local',
    bg: 'bg-emerald-400',
    text: 'text-emerald-950',
  },
};

const BANNER_HEIGHT = '1.75rem'; // 28px aprox

function detectEnvironment(): EnvKey | null {
  // Fonte primária: variável de build.
  const fromEnv = (import.meta.env.VITE_ENVIRONMENT as string | undefined)?.toLowerCase();
  if (fromEnv && fromEnv in ENV_CONFIG) return fromEnv as EnvKey;

  // Fallback por hostname para garantir sinalização em ambientes não-produção
  // mesmo sem VITE_ENVIRONMENT configurado.
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;

  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return 'development';
  }
  if (host.startsWith('stg.') || host.startsWith('staging.')) {
    return 'staging';
  }
  if (host.startsWith('qa.')) return 'qa';
  if (host.startsWith('demo.')) return 'demo';

  return null;
}

export function EnvironmentBanner() {
  const env = detectEnvironment();

  // Expõe a altura do banner via CSS variable e adiciona padding-top no body
  // para empurrar todo conteúdo. Outros elementos `fixed top-0` consomem
  // `var(--env-banner-h)` para se posicionarem abaixo.
  useEffect(() => {
    if (!env) return;
    const root = document.documentElement;
    root.style.setProperty('--env-banner-h', BANNER_HEIGHT);
    document.body.style.paddingTop = BANNER_HEIGHT;
    return () => {
      root.style.removeProperty('--env-banner-h');
      document.body.style.paddingTop = '';
    };
  }, [env]);

  if (!env) return null;

  const { label, bg, text } = ENV_CONFIG[env];

  return (
    <div
      role="status"
      aria-label={label}
      className={`fixed top-0 left-0 right-0 z-[100] ${bg} ${text} text-center text-xs font-medium py-1 px-3 flex items-center justify-center gap-2 shadow-sm`}
      style={{ height: BANNER_HEIGHT }}
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
